import { useEffect, useCallback } from 'react';
import { notificationsToRemove } from '../utils/badgeTray';

// ─────────────────────────────────────────────────────────────────────────────
// Keeps the app-icon badge honest.
//
// 🔑 THE ICON SHOWS THE BELL'S NUMBER (Jesse, 2026-10-28): the alerts not yet SEEN — GET /api/alerts/unread-count's
// count, the one server definition (backend constants/alertBadge.badgeAlertCount) the bell and every push payload
// (aps.badge, Android notificationCount) also carry. Synced when the bell's panel opens (→ 0), when an alert is
// opened, after "Clear all" (→ 0), and when the app comes to the foreground. A row's unread state (bold + dot until
// it is opened) is the list's, not the badge's. Where the Badge plugin is absent (the iOS 1.0.2 binary) every sync is
// a quiet no-op; it takes effect with the next native build.
//
// ⚠️ THE BADGE USED TO BE A LITERAL. The backend sent `aps: { badge: 1 }` — an
// ASSIGNMENT, not an increment — so the icon read "1" whether the borrower had
// one unread alert or four hundred and seventy, and NOTHING ever set it back to
// zero. A borrower could read every alert they had and still stare at a number.
//
// The server now sends a real count with each push. That alone is not enough:
// it is only correct at the instant a push lands. Anything that changes the
// unread count WITHOUT a push — the borrower reading their alerts, staff
// marking things read, a bulk cleanup — leaves the icon stale until the next
// notification happens to arrive.
//
// ⚠️ SO THE CLIENT RE-SYNCS RATHER THAN DECREMENTING. Counting down locally
// would drift the moment anything changed the count off-device, and drift in a
// counter is invisible: it looks like a number. Asking the server produces the
// same figure the alerts page shows, from the same query, every time.
//
// ⚠️ AND IT RE-SYNCS WHILE SIGNED OUT, WHICH IS THE CASE IT USED TO SKIP. No
// session means no borrower and no alerts list, so the count is a known zero —
// see the guard in syncBadge. Without that, a device with a badge and no login
// had no way to clear it short of deleting the app.
//
// Safe no-op in browser/PWA — never throws, and a failure leaves the existing
// badge alone rather than zeroing it, because a stale count is a smaller lie
// than a cleared one.
//
// ⚠️ THIS FIX SHIPS IN TWO PLACES ON TWO DIFFERENT SCHEDULES, WHICH IS WHY IT
// APPEARED "NOT LIVE" WHILE HALF OF IT WAS:
//
//   • THIS FILE — the client half — rides the OTA web bundle. Present on a
//     device as soon as it takes an update.
//   • backend/utils/sendPushNotification.js — the server half, which sends a
//     REAL count instead of a literal 1 — deploys to Render with the backend
//     and reaches devices only via the NEXT push.
//   • ⚠️ AND @capawesome/capacitor-badge IS A NATIVE DEPENDENCY. It cannot ride
//     OTA at all: it needs a native build and a store release. A device running
//     an older binary will import it and fail, which the catch below reports —
//     so until that build ships, an existing wrong badge stays wrong. iOS drops
//     the badge when an app is deleted, so a reinstall is the only manual clear.
//
// ⚠️⚠️ SETTLED ON DEVICE, 2026-09-09: THE PLUGIN IS NOT IN THE SHIPPED BINARY.
// Capacitor.isPluginAvailable('Badge') returned FALSE on the live App Store
// build (1.0.2, released 2026-08-28). So on every device running it, Badge.set
// is a no-op behind the isPluginAvailable guard below, and NOTHING can clear an
// app-icon badge — not the signed-out zero, not the resume sync, not a read of
// every alert. That is a shipped defect awaiting the next store release; it is
// NOT fixable by OTA, because the missing piece is native.
//
// ⚠️⚠️ AND THE "FALSIFIED cap-sync INFERENCE" RECORDED HERE WAS ITSELF WRONG —
// CORRECTED FROM AN ARTEFACT, 2026-09-09. This block used to say the reasoning
// "the package is in package.json, it declares capacitor.ios and ships a podspec,
// cap sync rewrites the pod list from node_modules, therefore the pod is compiled
// in" was persuasive and false. It is not false. A dry ios-build run
// (upload unticked) produced podfile-lock-asbuilt, and CapawesomeCapacitorBadge
// 8.0.2 is in it with a resolved checksum: cap sync DID add it, pod install DID
// resolve it.
//
// The binary lacks the plugin for a duller reason — THE BUILD PREDATES THE
// PACKAGE. The App Store 1.0.2 was submitted before 3a5778af (2026-08-27 04:23Z)
// added the dependency, so npm ci installed a tree without it and cap sync had
// nothing to add. Not an unexplained pipeline failure; a timing artefact.
//
// ⚠️ WHY THE CORRECTION MATTERS MORE THAN THE ORIGINAL NOTE: a wrong lesson
// recorded as a lesson is worse than none. Left standing, it taught the next
// reader to distrust reasoning that was actually sound, and to look for a
// cap-sync bug that does not exist.
//
// ⚠️ THE RULE THAT SURVIVES, NARROWED TO WHAT IT ACTUALLY SUPPORTS: package.json
// tells you what the NEXT build will contain, never what a SHIPPED binary
// contains — those differ whenever a dependency lands after a submission, which
// is common and invisible. To ask about a shipped binary, ask the binary:
// Capacitor.isPluginAvailable(<jsName>), the button in
// components/OtaUpdateReadout.jsx. To ask about the next build, read
// podfile-lock-asbuilt from a dry run — and CI now fails outright if a plugin
// declaring capacitor.ios never reaches the Podfile
// (.github/scripts/verify-ios-pods.js), so the question should stop arising.
//
// None of those three is visible from inside the app, so "is the badge fix
// live?" has three different answers depending on which half is being asked
// about. Say which when reporting on it.
// ─────────────────────────────────────────────────────────────────────────────

const isNative = () =>
  typeof window !== 'undefined' && !!(window.Capacitor?.isNativePlatform?.());

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

async function setBadge(count, token = null) {
  // ⚠️ THREE DIFFERENT ABSENCES, AND THEY NEED THREE DIFFERENT ANSWERS. Getting
  // this wrong once already cost a badge that never worked at all.
  //
  //   1. BUILD-TIME — the package is not installed / not bundled. That is a
  //      packaging error and MUST THROW where someone sees it. The dynamic
  //      import below is what surfaces it.
  //   2. RUNTIME — the package IS bundled, but the native binary on this device
  //      predates it (an OTA web layer can move ahead of the shell; that is what
  //      Capgo's native-mismatch warning is telling us). Legitimate degradation:
  //      skip quietly, do not throw, do not warn on every sync.
  //   3. DEVICE REFUSAL — plugin present, device says no (permission denied,
  //      platform without a badge concept). Caught and logged.
  //
  // ⚠️ Capacitor.isPluginAvailable ASKS THE NATIVE BRIDGE whether THIS BUILD
  // registered the plugin. That is categorically different from the
  // `typeof Badge.set === "function"` check this file used to carry: typeof
  // inspects an object the bundler always provides, so it answered "no" for
  // case 1 exactly as loudly as for case 2 — which is to say, silently, and the
  // badge went a month never being set. isPluginAvailable separates them.
  const { Capacitor } = await import('@capacitor/core');
  // The tray first, and whatever the Badge plugin's state (it is absent from today's binaries).
  await syncTray(count, token, Capacitor);
  if (!Capacitor.isPluginAvailable('Badge')) {
    // Case 2. info, not warn: this is expected on every device still on a
    // pre-badge binary, and a warning per sync would train people to ignore it.
    console.info('[Badge] native build predates the Badge plugin — skipping until the next store release');
    return;
  }

  try {
    // ⚠️ THE IMPORT IS INSIDE THE TRY. It used to sit above it, where a rejection
    // was caught only by syncBadge's outer catch — covered by luck rather than
    // design, which is the exact shape this codebase has spent two days closing.
    const { Badge } = await import('@capawesome/capacitor-badge');
    await Badge.set({ count });
  } catch (e) {
    // Case 3.
    console.warn('[Badge] device refused to set badge:', e?.message || e);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE TRAY, ON TODAY'S BINARIES (Hannah, 2026-11-01 — the icon kept its count after the alerts were read). The tray is
// cleaned through @capacitor-firebase/messaging, which IS in the shipped iOS 1.0.2 and Android 1.0.4 binaries, so it no
// longer waits behind the Badge plugin (which is not):
//   · count 0 → every delivered notification goes (both platforms; as before, but it used to run only when Badge existed);
//   · Android, count > 0 → the delivered notifications of alerts already seen or read go (utils/badgeTray, matched on the
//     alert id the push carries). Most Android launchers drop the icon badge with the notifications.
// iOS's icon number is set by the server's badge-only push when the count falls (backend utils/badgeSync).
// Never throws; a failure leaves the tray as it is.
// ─────────────────────────────────────────────────────────────────────────────
async function syncTray(count, token, Capacitor) {
  try {
    if (!Capacitor.isPluginAvailable('FirebaseMessaging')) return;
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
    if (count === 0) {
      await FirebaseMessaging.removeAllDeliveredNotifications();
      return;
    }
    if (Capacitor.getPlatform() !== 'android' || !token) return;
    const res = await fetch(`${API}/api/alerts?limit=100`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    const list = await res.json();
    const alerts = Array.isArray(list.alerts) ? list.alerts : [];
    const { notifications } = await FirebaseMessaging.getDeliveredNotifications();
    const remove = notificationsToRemove(notifications, alerts, { complete: Number(list.total) <= alerts.length });
    if (remove.length) await FirebaseMessaging.removeDeliveredNotifications({ notifications: remove });
  } catch (e) {
    console.warn('[Badge] tray not synced:', e?.message || e);
  }
}

export function useAppBadge(token) {
  const syncBadge = useCallback(async () => {
    if (!isNative()) return;

    // ⚠️ SIGNED OUT IS A KNOWN COUNT, NOT AN UNKNOWN ONE. This guard used to
    // read `if (!isNative() || !token) return`, which meant a signed-out device
    // could NEVER correct its own badge — not on launch, not on resume, not
    // ever. The only escape was deleting the app. That is the wrong shape: with
    // no session there is no borrower, no alerts list to open, and therefore
    // nothing a number on the icon could be counting. Zero is the correct
    // answer, and it is DERIVED rather than guessed.
    //
    // ⚠️ IT IS NOT THE SAME CASE AS A FAILED FETCH, WHICH STILL LEAVES THE
    // BADGE ALONE (see the catch below). There the count is genuinely unknown
    // and a stale number is a smaller lie than a cleared one. Here it is known.
    // Collapsing the two is what made "leave it alone" look like the safe
    // default in both.
    //
    // ⚠️ NO RESTORE WINDOW TO RACE. AuthContext seeds `token` synchronously
    // from localStorage in a useState initialiser, so a signed-in app never
    // renders one frame with a null token — this cannot clear a real badge on
    // launch and then re-fetch it. Do NOT add an authLoading gate here; that
    // flag is about the user profile, not the token, and gating on it would
    // reintroduce exactly the never-clears state above.
    if (!token) {
      await setBadge(0);
      return;
    }

    try {
      const res = await fetch(`${API}/api/alerts/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const { count } = await res.json();
      if (typeof count === 'number' && count >= 0) await setBadge(count, token);
    } catch (_) {
      /* offline or server down — leave the badge as it is */
    }
  }, [token]);

  useEffect(() => {
    // ⚠️ NOT GATED ON `token` EITHER, AND FOR THE SAME REASON THE CALLBACK
    // ISN'T. A badge can arrive on a signed-out device — a token row survives
    // an app delete and a logout that never reached the server, so a push can
    // still be addressed to it — and the resume listener is the only thing that
    // would ever clear it. Gating the effect on a session meant the one state
    // that most needs correcting was the one state that never got it.
    if (!isNative()) return undefined;

    // On mount / login / logout.
    syncBadge();

    // ⚠️ AND ON RESUME. This is the case that matters most: the borrower taps
    // the notification, reads the alert, backgrounds the app, comes back. No
    // push arrives to correct anything, so without a resume sync the icon keeps
    // whatever the last push set.
    let remove = () => {};
    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const h = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) syncBadge();
        });
        remove = () => h.remove();
      } catch (_) { /* not native */ }
    })();

    return () => remove();
  }, [token, syncBadge]);

  return syncBadge;
}
